import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import type { TruckingPosition, TruckingPositionsEnvelope, TruckingPositionWriteEnvelope } from '../../types/trucking-position';
import { Tooltip } from '../../components/ui/tooltip';

const inputCls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50';

/*
 * Trucking positions — the per-company driver and equipment types (Bed Truck
 * Operator, Winch Tractor Operator, …) behind the driver profile, the Edit Pay
 * Rates screen, BOL line items and dispatch equipment type. Stored in
 * trucking_positions.
 *
 * This used to be the Positions tab under People & access, where every tenant
 * saw it. It is trucking data, so it lives on the Trucking tab now, which only
 * a trucking tenant is shown. It is NOT what users.position_id points at —
 * that is `positions`, edited from the employee profile.
 *
 * Every write (POST, PUT, DELETE, reorder) is non-strict requireRole('admin')
 * since api#736, so `canWrite` comes from useCanWriteCompanySettings. The list
 * is ungated and stays readable to everyone.
 */
export default function TruckingPositionsSection({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient();

  const [addName, setAddName] = useState('');
  const [addCode, setAddCode] = useState('');
  const [addCodeManual, setAddCodeManual] = useState(false);
  const [addDesc, setAddDesc] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editCode, setEditCode] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [saving, setSaving] = useState(false);

  // Same key as DriverManagementScreen's read, so an edit here reaches it.
  // camelCase on the way in (raw Drizzle rows), snake_case on the way out.
  const { data: positions = [], isLoading } = useQuery({
    queryKey: ['trucking-positions-all'],
    queryFn: () => apiClient.get<TruckingPositionsEnvelope>('/trucking/positions/all').then(r => unwrap<TruckingPosition[]>(r.data) ?? []),
    retry: false,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['trucking-positions-all'] });
  const fail = (err: any, fallback: string) => toast.error(err?.response?.data?.error || fallback);

  const handleAdd = async () => {
    if (!addName.trim()) return;
    setSaving(true);
    try {
      await apiClient.post<TruckingPositionWriteEnvelope>('/trucking/positions', {
        name: addName.trim(),
        short_code: addCode.trim() || undefined,
        description: addDesc.trim() || undefined,
      });
      setAddName(''); setAddCode(''); setAddCodeManual(false); setAddDesc('');
      refresh();
    } catch (err: any) {
      fail(err, 'Failed to add position');
    } finally { setSaving(false); }
  };

  const handleUpdate = async (id: string) => {
    setSaving(true);
    try {
      await apiClient.put<TruckingPositionWriteEnvelope>(`/trucking/positions/${id}`, {
        name: editName.trim(), short_code: editCode.trim(), description: editDesc.trim(),
      });
      setEditId(null); refresh();
    } catch (err: any) {
      fail(err, 'Failed to update');
    } finally { setSaving(false); }
  };

  const handleToggle = async (id: string, currentActive: boolean) => {
    try {
      await apiClient.put<TruckingPositionWriteEnvelope>(`/trucking/positions/${id}`, { is_active: !currentActive });
      refresh();
    } catch (err: any) {
      fail(err, 'Failed to update');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this position? This only works if no drivers or BOLs reference it.')) return;
    try {
      await apiClient.delete<TruckingPositionWriteEnvelope>(`/trucking/positions/${id}`);
      refresh();
    } catch (err: any) {
      fail(err, 'Cannot delete — deactivate instead');
    }
  };

  const handleMoveUp = async (idx: number) => {
    if (idx === 0) return;
    const items = [...positions];
    [items[idx - 1], items[idx]] = [items[idx], items[idx - 1]];
    const reorder = items.map((p, i) => ({ id: p.id, sort_order: i }));
    try {
      await apiClient.post<TruckingPositionWriteEnvelope>('/trucking/positions/reorder', { items: reorder });
      refresh();
    } catch (err: any) {
      fail(err, 'Failed to reorder');
    }
  };

  return (
    <section className="space-y-3" aria-labelledby="trucking-positions-heading">
      <div>
        <h3 id="trucking-positions-heading" className="text-sm font-semibold text-white">Positions</h3>
        <p className="text-xs text-fieldiq-muted mt-0.5">
          Driver and equipment types used in pay rates, BOLs, and dispatch. Each change saves on its own.
        </p>
        {!canWrite && (
          <p className="text-xs text-fieldiq-muted mt-1">Only an admin can change trucking positions.</p>
        )}
      </div>

      {isLoading && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" /></div>}

      {!isLoading && positions.length === 0 && (
        <div className="text-center py-12 text-fieldiq-muted text-sm">No positions yet</div>
      )}

      {positions.map((p, idx) => {
        // The column is nullable with a true default; only an explicit false is inactive.
        const active = p.isActive !== false;
        return (
        <div key={p.id} className={`bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 flex items-center gap-3 ${!active ? 'opacity-50' : ''}`}>
          {canWrite && (
            <Tooltip content="Move up">
              <button onClick={() => handleMoveUp(idx)} className="text-fieldiq-text hover:text-white text-xs" aria-label={`Move ${p.name} up`}>≡</button>
            </Tooltip>
          )}
          {editId === p.id ? (
            <div className="flex-1 flex items-center gap-2">
              <input value={editName} onChange={e => setEditName(e.target.value)} className={inputCls + ' flex-1'} placeholder="Position name" />
              <input value={editCode} onChange={e => setEditCode(e.target.value)} className={inputCls + ' w-20'} placeholder="Code" />
              <input value={editDesc} onChange={e => setEditDesc(e.target.value)} className={inputCls + ' flex-1'} placeholder="Description" />
              <button onClick={() => handleUpdate(p.id)} disabled={saving} className="px-3 py-1.5 text-xs font-semibold bg-fieldiq-accent text-white rounded-lg">Save</button>
              <button onClick={() => setEditId(null)} className="px-3 py-1.5 text-xs text-fieldiq-text">Cancel</button>
            </div>
          ) : (
            <>
              <span className="text-sm font-medium text-white flex-1">{p.name}</span>
              <span className="text-xs text-fieldiq-muted font-mono w-12">{p.shortCode || '—'}</span>
              {!active && !canWrite && <span className="text-xs text-fieldiq-muted">Inactive</span>}
              {canWrite && (
                <>
                  <button onClick={() => { setEditId(p.id); setEditName(p.name); setEditCode(p.shortCode || ''); setEditDesc(p.description || ''); }} className="px-2 py-1 text-xs text-fieldiq-accent hover:text-white">Edit</button>
                  <button onClick={() => handleToggle(p.id, active)} className={`px-2 py-1 text-xs ${active ? 'text-amber-400' : 'text-green-400'}`}>{active ? 'Deactivate' : 'Activate'}</button>
                  <button onClick={() => handleDelete(p.id)} className="px-2 py-1 text-xs text-red-400 hover:text-red-300" aria-label={`Delete ${p.name}`}>×</button>
                </>
              )}
            </>
          )}
        </div>
        );
      })}

      {canWrite && (
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
          <p className="text-sm font-medium text-white mb-3">Add Position</p>
          <div className="flex items-center gap-2">
            <input value={addName} onChange={e => { setAddName(e.target.value); if (!addCodeManual) setAddCode(e.target.value.split(/\s+/).filter(Boolean).map(w => w[0]?.toUpperCase()).join('').slice(0, 5)); }} className={inputCls + ' flex-1'} placeholder="Position name" />
            <input value={addCode} onChange={e => { setAddCode(e.target.value.toUpperCase().slice(0, 5)); setAddCodeManual(true); }} className={inputCls + ' w-20'} placeholder="Code" maxLength={5} />
            <input value={addDesc} onChange={e => setAddDesc(e.target.value)} className={inputCls + ' flex-1'} placeholder="Description (optional)" />
            <button onClick={handleAdd} disabled={saving || !addName.trim()} className="px-4 py-2 text-xs font-semibold bg-fieldiq-accent text-white rounded-lg disabled:opacity-50 flex items-center gap-1">
              <Plus size={14} /> Add
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
